import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
// logic
import { Form, Formik } from 'formik';
import useFilterViewForm from './useFilterViewForm';
import useAssignAgentModal from '../modals/useAssignAgentModal';
import useShippersFilter from '../ticketForm/useShippersFilter';
// Icons
import { ReactComponent as RemoveCross } from 'assets/icons/remove-cross.svg';
// components
import TicketFormContainer from 'modules/emergencies/components/ui/containers/ticketFormContainer';
import PremiumSectionContainer from '../ticketForm/components/PremiumSectionContainer';
import PremiumSection from '../ticketForm/components/PremiumSection';
import { SectionContainer } from '../ticketForm/components/SectionContainer';
import { SectionBody } from '../ticketForm/components/SectionBody';
import SectionTitle from '../ticketForm/components/SectionTitle';
import { SectionLine } from '../ticketForm/components/SectionLine';
import FooterButtonContainer from '../ticketForm/components/FooterButtonContainer';
import { CustomSticky } from '../ui/sticky/CustomSticky';
import { FormButton } from '../ui/buttons/FormButton';
import { Half } from '../ticketForm/components/Half';
// inputs
import { FormikPremiumInput } from '../ui/inputs/customInput';
import SelectWithSearchInput from '../modals/SelectWithSearchInput';
import { CheckboxsContainer } from '../ticketForm/components/CheckboxsContainer';
import Checkbox from '../ticketForm/components/Checkbox';
import { QuoteFormErrorInfoBlock } from '../quoteForm/blocks/quoteFormErrorInfoBlock';
import { useWorkingAreas } from './useWorkingAreas';

const RemoveCrossGrey = styled(RemoveCross)`
  path {
    fill: #94a6b8;
  }
`;

const InputListContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  width: 100%;
`;

const InputListInputContainer = styled.div`
  width: 194px;
`;

const InputListItemsContainer = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  border-radius: 4px;
  border: 1px solid var(--grey200);
  background: var(--grey200);
  width: 100;
  height: 100%;
`;

const InputListsItemsContainerInnerContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 4px;
  flex: 1 0 0;
  flex-wrap: wrap;
`;

const InputListItemLabel = styled.div`
  color: var(--grey500);
  text-align: center;
  font-feature-settings: 'clig' off, 'liga' off;

  /* 14px/regular */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
`;

const InputListItemsContainerInnerItem = styled.div`
  display: flex;
  height: 24px;
  padding: 0px 4px 0px 8px;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: var(--grey500);
  cursor: pointer;
`;

const InputListItemsContainerInnerItemText = styled.div`
  color: var(--secondary-white, #fff);
  font-feature-settings: 'clig' off, 'liga' off;

  /* 14px/regular */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
`;

const InputListItemsContainerClearIcon = styled.div`
  display: flex;
  cursor: pointer;
`;

const t = {
  assigned_to: {
    error: 'No agents found, try again later',
    no_users: 'No agents found',
    field_label: 'Add filter by agent',
    search_label: 'Search agent',
  },
  working_area_id: {
    error: 'No working areas found, try again later',
    no_users: 'No working areas found',
    field_label: 'Add filter by working area',
    search_label: 'Search working areas',
  },
  shipper_id: {
    error: 'No customers found, try again later',
    no_users: 'No customers found',
    field_label: 'Add filter by customers',
    search_label: 'Search customers',
  },
  origin_country_code: {
    error: 'No groups found, try again later',
    no_users: 'No groups found',
    field_label: 'Add filter by origin',
    search_label: 'Search groups',
  },
  destination_country_code: {
    error: 'No groups found, try again later',
    no_users: 'No groups found',
    field_label: 'Add filter by destination',
    search_label: 'Search groups',
  },
  back: 'Back',
};

const vehicleOptions = [
  {
    value: 'LTL',
    label: 'LTL',
  },
  {
    value: 'FTL',
    label: 'FTL',
  },
].map((vehicle) => ({ search: vehicle.label, display: vehicle.label, ...vehicle }));

const FilterViewForm = ({ filterViewFormData, className }) => {
  const { onSubmit, validationSchema, initialValues } = filterViewFormData;

  const { showErrors, setShowErrors, isWaitingForResponse, backendErrors, beforeOnSubmit, goBack } =
    useFilterViewForm({ onSubmit });

  const { usersOptions, isLoadingUsersToAssign, assignAgentError } = useAssignAgentModal();

  const { shipperOptions, shipperOptionsError, isLoadingShipperOptions, setSearch } =
    useShippersFilter({
      initialQuery: '',
    });

  const [savedShippers, setSavedShippers] = useState([]);

  const { workingAreasOptions, isLoadingWorkingAreas, workingAreasError } = useWorkingAreas();

  return (
    <TicketFormContainer className={className}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={beforeOnSubmit()}
      >
        {({ errors, dirty, values, submitForm: submitFormikForm, setFieldValue }) => {
          const hasBackendErrors = Object.keys(backendErrors).length > 0;
          let backendErrorsMsg = 'There was an error creating the filter view user';
          if (!!backendErrors.username) backendErrorsMsg = 'There email is already in use';
          if (backendErrors.details === 'can_not_reach_backend')
            backendErrorsMsg = 'There was an error connecting to the server';
          const assigned_to = values.assigned_to;
          const working_area_id = values.working_area_id;
          const shipper_id = values.shipper_id;
          return (
            <Form>
              {/* body */}
              <PremiumSectionContainer>
                <PremiumSection>
                  {/* View name */}
                  <SectionContainer>
                    <SectionTitle>View name</SectionTitle>
                    <SectionBody>
                      <SectionLine>
                        <FormikPremiumInput
                          type="text"
                          name="name"
                          label="View name"
                          showErrors={showErrors}
                          isRequired={true}
                        />
                      </SectionLine>
                    </SectionBody>
                  </SectionContainer>
                  {/* assigned agent */}
                  <SectionContainer>
                    <SectionTitle>Assigned agent</SectionTitle>
                    <SectionBody>
                      <SectionLine>
                        <InputListContainer>
                          <InputListInputContainer>
                            <SelectWithSearchInput
                              onFieldChange={(value) => {
                                const agentOption = usersOptions.find(
                                  (option) => option.value === value
                                );
                                if (agentOption) {
                                  const set = new Set(assigned_to);
                                  set.add(agentOption.value);
                                  const newSelectedAgents = Array.from(set);
                                  setFieldValue('assigned_to', newSelectedAgents);
                                }
                              }}
                              options={usersOptions}
                              isLoadingOptions={isLoadingUsersToAssign}
                              value={''}
                              label={t.assigned_to.field_label}
                              searchLabel={t.assigned_to.search_label}
                              optionsEmptyMssg={t.assigned_to.no_users}
                              isClereable={true}
                              key={assigned_to}
                              hasError={assignAgentError}
                            />
                          </InputListInputContainer>
                          <InputListItemsContainer>
                            <InputListsItemsContainerInnerContainer>
                              {assigned_to.length === 0 && (
                                <InputListItemLabel>No agents selected</InputListItemLabel>
                              )}

                              {assigned_to.map((agentId) => {
                                const agentUser = usersOptions.find(
                                  (option) => option.value === agentId
                                );
                                return (
                                  <InputListItemsContainerInnerItem
                                    key={agentId}
                                    onClick={() => {
                                      const set = new Set(assigned_to);
                                      set.delete(agentId);
                                      const newSelectedAgents = Array.from(set);
                                      setFieldValue('assigned_to', newSelectedAgents);
                                    }}
                                  >
                                    <InputListItemsContainerInnerItemText>
                                      {agentUser.label}
                                    </InputListItemsContainerInnerItemText>
                                    <RemoveCross />
                                  </InputListItemsContainerInnerItem>
                                );
                              })}
                            </InputListsItemsContainerInnerContainer>
                            {assigned_to.length > 0 && (
                              <InputListItemsContainerClearIcon
                                onClick={() => {
                                  setFieldValue('assigned_to', []);
                                }}
                              >
                                <RemoveCrossGrey />
                              </InputListItemsContainerClearIcon>
                            )}
                          </InputListItemsContainer>
                        </InputListContainer>
                      </SectionLine>
                    </SectionBody>
                  </SectionContainer>
                  {/* working area*/}
                  <SectionContainer>
                    <SectionTitle>Working area</SectionTitle>
                    <SectionBody>
                      <SectionLine>
                        <InputListContainer>
                          <InputListInputContainer>
                            <SelectWithSearchInput
                              onFieldChange={(value) => {
                                const workingAreaOption = workingAreasOptions.find(
                                  (option) => option.value === value
                                );
                                if (workingAreaOption) {
                                  const set = new Set(working_area_id);
                                  set.add(workingAreaOption.value);
                                  const newSelectedWorkingAreas = Array.from(set);
                                  setFieldValue('working_area_id', newSelectedWorkingAreas);
                                }
                              }}
                              options={workingAreasOptions}
                              isLoadingOptions={isLoadingWorkingAreas}
                              value={''}
                              label={t.working_area_id.field_label}
                              searchLabel={t.working_area_id.search_label}
                              optionsEmptyMssg={t.working_area_id.no_users}
                              isClereable={true}
                              key={'working_area_id'}
                              hasError={workingAreasError}
                            />
                          </InputListInputContainer>
                          <InputListItemsContainer>
                            <InputListsItemsContainerInnerContainer>
                              {working_area_id.length === 0 && (
                                <InputListItemLabel>No working areas selected</InputListItemLabel>
                              )}

                              {working_area_id.map((workingAreaId) => {
                                const workingArea = workingAreasOptions.find(
                                  (option) => option.value === workingAreaId
                                );
                                return (
                                  <InputListItemsContainerInnerItem
                                    key={workingAreaId}
                                    onClick={() => {
                                      const set = new Set(working_area_id);
                                      set.delete(workingAreaId);
                                      const newSelectedWorkingAreas = Array.from(set);
                                      setFieldValue('working_area_id', newSelectedWorkingAreas);
                                    }}
                                  >
                                    <InputListItemsContainerInnerItemText>
                                      {workingArea.label}
                                    </InputListItemsContainerInnerItemText>
                                    <RemoveCross />
                                  </InputListItemsContainerInnerItem>
                                );
                              })}
                            </InputListsItemsContainerInnerContainer>
                            {working_area_id.length > 0 && (
                              <InputListItemsContainerClearIcon
                                onClick={() => {
                                  setFieldValue('working_area_id', []);
                                }}
                              >
                                <RemoveCrossGrey />
                              </InputListItemsContainerClearIcon>
                            )}
                          </InputListItemsContainer>
                        </InputListContainer>
                      </SectionLine>
                    </SectionBody>
                  </SectionContainer>
                  {/* shipper */}
                  <SectionContainer>
                    <SectionTitle>Customer</SectionTitle>
                    <SectionBody>
                      <SectionLine>
                        <InputListContainer>
                          <InputListInputContainer>
                            <SelectWithSearchInput
                              onFieldChange={(value) => {
                                const shipperOption = shipperOptions.find(
                                  (option) => option.value === value
                                );
                                if (shipperOption) {
                                  const set = new Set(shipper_id);
                                  set.add(shipperOption.value);
                                  const newSelectedShippers = Array.from(set);
                                  setFieldValue('shipper_id', newSelectedShippers);
                                  const newSavedShippers = [...savedShippers, shipperOption];
                                  setSavedShippers(newSavedShippers);
                                }
                              }}
                              onSearchChange={setSearch}
                              options={shipperOptions}
                              isLoadingOptions={isLoadingShipperOptions}
                              value={''}
                              label={t.shipper_id.field_label}
                              searchLabel={t.shipper_id.search_label}
                              optionsEmptyMssg={t.shipper_id.no_users}
                              isClereable={true}
                              key={shipper_id}
                              hasError={shipperOptionsError}
                            />
                          </InputListInputContainer>
                          <InputListItemsContainer>
                            <InputListsItemsContainerInnerContainer>
                              {shipper_id.length === 0 && (
                                <InputListItemLabel>No customers selected</InputListItemLabel>
                              )}
                              {shipper_id.map((shipperId) => {
                                const shipperUser = savedShippers.find(
                                  (option) => option.value === shipperId
                                );
                                return (
                                  <InputListItemsContainerInnerItem
                                    key={shipperId}
                                    onClick={() => {
                                      const set = new Set(shipper_id);
                                      set.delete(shipperId);
                                      const newSelectedShippers = Array.from(set);
                                      setFieldValue('shipper_id', newSelectedShippers);
                                      const newSavedShippers = savedShippers.filter(
                                        (shipper) => shipper.value !== shipperId
                                      );
                                      setSavedShippers(newSavedShippers);
                                    }}
                                  >
                                    <InputListItemsContainerInnerItemText>
                                      {shipperUser.label}
                                    </InputListItemsContainerInnerItemText>
                                    <RemoveCross />
                                  </InputListItemsContainerInnerItem>
                                );
                              })}
                            </InputListsItemsContainerInnerContainer>
                            {shipper_id.length > 0 && (
                              <InputListItemsContainerClearIcon
                                onClick={() => {
                                  setFieldValue('shipper_id', []);
                                  setSavedShippers([]);
                                }}
                              >
                                <RemoveCrossGrey />
                              </InputListItemsContainerClearIcon>
                            )}
                          </InputListItemsContainer>
                        </InputListContainer>
                      </SectionLine>
                    </SectionBody>
                  </SectionContainer>
                  {/* vehicle */}
                  <SectionContainer>
                    <SectionTitle>Vehicle types</SectionTitle>
                    <SectionBody>
                      <SectionLine>
                        <InputListContainer>
                          <InputListInputContainer>
                            <CheckboxsContainer>
                              {vehicleOptions.map((s) => (
                                <Checkbox
                                  key={s.value}
                                  name="vehicle_type"
                                  value={s.value}
                                  label={s.label}
                                  backgroundVariant={'grey200'}
                                />
                              ))}
                            </CheckboxsContainer>
                          </InputListInputContainer>
                        </InputListContainer>
                      </SectionLine>
                    </SectionBody>
                  </SectionContainer>
                </PremiumSection>
              </PremiumSectionContainer>
              {hasBackendErrors && <QuoteFormErrorInfoBlock backendErrorsMsg={backendErrorsMsg} />}
              {/* footer */}
              <CustomSticky>
                <FooterButtonContainer>
                  <Half>
                    <FormButton variant="ghost" onClick={goBack}>
                      Back {hasBackendErrors && <> ⚠️ </>}
                    </FormButton>
                  </Half>
                  <Half>
                    <FormButton
                      isDisabled={isWaitingForResponse}
                      onClick={() => {
                        setShowErrors(true);
                        submitFormikForm();
                      }}
                    >
                      Create new view {hasBackendErrors && <> ⚠️ </>}
                    </FormButton>
                  </Half>
                </FooterButtonContainer>
              </CustomSticky>
            </Form>
          );
        }}
      </Formik>
    </TicketFormContainer>
  );
};

export default injectIntl(FilterViewForm);
